import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Button, FormGroup, Label, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { fetchEmployeeDropdown, getBankStatementDetails, clearBankStatementDetails, clearapproveError1 } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import * as Yup from "yup";
import Select from "react-select";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomMonthPicker from "common/CustomMonthPicker";
import TableContainer from "components/Common/TableContainer";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";
import Swal from 'sweetalert2';

const PayrollReportBankStatement = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);

  const {
    dropdownData,
    bankStatementDetails,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    dropdownData: state.EmployeeDetailsReducer?.dropdownData ?? {},
    bankStatementDetails: state.PayrollReportsReducer?.bankStatementDetails ?? [],
    loading: state.PayrollReportsReducer?.loading,
    error: state.PayrollReportsReducer?.error,
    success: state.PayrollReportsReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0]?.unit_id
    dispatch(fetchEmployeeDropdown(unitId));
  }, [dispatch]);

  const categoryOptions = dropdownData?.category_list?.map((item) => ({ label: item.category_name, value: item.category_id, })) || [];
  const departmentOptions = dropdownData?.department_list?.map((item) => ({ label: item.depart_name, value: item.depart_id, })) || [];
  const employeeTypeOptions = dropdownData?.empment_type?.map((item) => ({ label: item.emp_type, value: item.emptype_id, })) || [];
  const salaryTypeOptions = dropdownData?.salary_type?.map((item) => ({ label: item.wage_type, value: item.wage_typeid, })) || [];

  const formik = useFormik({
    initialValues: {
      category: "",
      department: "",
      employeeType: "",
      salaryType: "",
      salaryMonth: "",
      bankName: "",
      bankStatementDetails: [],
    },
    validationSchema: Yup.object({
      salaryMonth: Yup.string().nullable().typeError("Month is required").required("Month is required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("unit_id", authDetails?.unit_id || 0);
      formData.append("emp_cat", values.category || 0);
      formData.append("emp_dep", values.department || 0);
      formData.append("emp_type", values.employeeType || 0);
      formData.append("sal_type", values.salaryType || 0);
      formData.append("sal_month", values.salaryMonth || "");

      dispatch(getBankStatementDetails(formData));
    },
  });

  const columns = [
    { Header: "S.NO", Cell: ({ row }) => bankStatementDetails.length - row.index, className: "serial-no text-center", applyHeaderClass: true },
    { Header: "CODE", accessor: "Code" },
    { Header: "NAME", accessor: "emp_name" },
    { Header: "ACCOUNT_NUMBER", accessor: "account_no" },
    { Header: "BANK", accessor: "bank" },
    { Header: "BRANCH", accessor: "branch" },
    { Header: "IFSC_CODE", accessor: "ifsc_code" },
    {
      Header: "NET_AMOUNT", accessor: "net_amount", className: "text-center",
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + Number(row.values.net_amount || 0), 0);
        return (
          <div style={{ fontWeight: "700", color: "#0c0c0c", backgroundColor: "#f2f7f5", padding: "6px", textAlign: "center", width: "100%" }}>
            ₹ {total.toLocaleString()}
          </div>
        );
      }
    },
  ];

  useEffect(() => {
    return () => {
      dispatch(clearapproveError1());
    };
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Payroll / Reports / Salary Reports" breadcrumbItem="Bank Statement" />
        <Card>
          <CardBody>
            <form onSubmit={formik.handleSubmit}>
              <Row className="gy-3">
                <Col md={4}>
                  <Label>Category</Label>
                  <Select
                    options={categoryOptions}
                    isClearable={true}
                    name="category"
                    value={categoryOptions.find((opt) => opt.value === formik.values.category) || null}
                    onChange={(selected) => formik.setFieldValue("category", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>

                <Col md={4}>
                  <Label>Department</Label>
                  <Select
                    options={departmentOptions}
                    name="department"
                    isClearable={true}
                    value={departmentOptions.find((opt) => opt.value === formik.values.department) || null}
                    onChange={(selected) => formik.setFieldValue("department", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>

                <Col md={4}>
                  <Label>Employee Type</Label>
                  <Select
                    options={employeeTypeOptions}
                    name="employeeType"
                    isClearable={true}
                    value={employeeTypeOptions.find((opt) => opt.value === formik.values.employeeType) || null}
                    onChange={(selected) => formik.setFieldValue("employeeType", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>

                <Col md={4}>
                  <Label>Salary Type</Label>
                  <Select
                    options={salaryTypeOptions}
                    isClearable={true}
                    name="salaryType"
                    value={salaryTypeOptions.find((opt) => opt.value === formik.values.salaryType) || null}
                    onChange={(selected) => formik.setFieldValue("salaryType", selected ? selected.value : "")}
                    placeholder="Select"
                  />
                </Col>

                <Col md={4}>
                  <Label className="form-label">Salary Month <span className="text-danger">*</span></Label>
                  <CustomMonthPicker
                    name="month"
                    value={formik.values.salaryMonth}
                    onChange={(date) => { formik.setFieldValue("salaryMonth", date); }}
                    onBlur={() => { console.log("Blurred! Value is:", formik.values.salaryMonth); }}
                    disableFuture={true}
                    className="form-control form-control-sm"
                    classNamePrefix="react-select"
                    isClearable={true}
                  />
                  {formik.touched.salaryMonth && formik.errors.salaryMonth && (
                    <div className="text-danger mt-1">{formik.errors.salaryMonth}</div>
                  )}
                </Col>

                <Col md={4}>
                  <div className="d-flex mt-3">
                    <Button type="submit" className="btn button-search mt-3 h-100"> <i className="fas fa-search me-1" /> SEARCH</Button>
                    {bankStatementDetails && bankStatementDetails.length > 0 && (
                      <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                        <DropdownToggle color="primary" className="btn button-export mt-3 ms-2"> <i className="fas fa-file-export me-1" /> EXPORT</DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem onClick={() => exportEmployeeToExcel(bankStatementDetails, authDetails?.unit_fullname, authDetails?.unit_address, "Bank Statement Report")}>
                            <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                          </DropdownItem>
                          <DropdownItem onClick={() => exportEmployeeToPDF(bankStatementDetails, authDetails?.unit_fullname, authDetails?.unit_address, "Bank Statement Report", columns)}>
                            <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    )}
                    <Button type="reset" className="btn button-cancel mt-3 ms-2 h-100"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?", text: "Unsaved clear will be lost!", icon: "warning",
                          showCancelButton: true, confirmButtonText: "Yes, Clear", cancelButtonText: "No", confirmButtonColor: "#3085d6", cancelButtonColor: "#d33"
                        }).then((result) => {
                          if (result.isConfirmed) {
                            formik.handleReset();
                            dispatch(clearBankStatementDetails());
                          }
                        });
                      }}
                    >
                      <i className="fas fa-sync me-1" /> CANCEL
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TableContainer columns={columns} data={bankStatementDetails || []} isGlobalFilter={true} customPageSize={10} showFooter={true} />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PayrollReportBankStatement;
